import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect, useCallback } from 'react';

import {
  Box,
  Tab,
  Grid,
  Tabs,
  Link,
  Switch,
  TextField,
  Autocomplete,
  FormControlLabel,
} from '@mui/material';

import { useModal } from 'src/hooks/useModal';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { fetchTagsByDepartment } from 'src/services/tag.service';

import FileInput from 'src/components/inputs/fileInput/FileInput';

import OptionSelector from '../OptionsSelector';
import { useFormContext } from '../../../../hooks/useForm';
import AddUpdateTags from '../../../tags/modals/AddUpdateTags';
import TextInput from '../../../../components/FormInputs/TextInput';
import SelectInput from '../../../../components/FormInputs/SelectInput';
import TextAreaInput from '../../../../components/FormInputs/TextAreaInput';
import DateTimePickerInput from '../../../../components/FormInputs/DateTimePickerInput';

const InputPlaceHolder = ({ selectedInput, question, sectionId, redirectOnAnswer }) => {
  const { t } = useTranslation();
  const commonSx = { mt: 2 };

  if (selectedInput === 'TextInput')
    return <TextInput label="Text input" disabled size="small" sx={commonSx} />;
  if (selectedInput === 'TextAreaInput')
    return <TextAreaInput label="Long text" disabled size="small" sx={commonSx} />;
  if (selectedInput === 'DateTimePickerInput')
    return <DateTimePickerInput disabled size="small" sx={commonSx} />;
  if (selectedInput === 'FileInput')
    return <FileInput disabled size="small" sx={commonSx} />;

  return (
    <OptionSelector
      question={question}
      sectionId={sectionId}
      selectedInput={selectedInput}
      // Re-establishes the exact string values ('goToSectionBaseOn' or 'next') to trigger the visibility rules
      selectedType={redirectOnAnswer ? t('pages.formPage.goToSectionBaseOn') : 'next'}
    />
  );
};

InputPlaceHolder.propTypes = {
  selectedInput: PropTypes.string,
  question: PropTypes.object,
  sectionId: PropTypes.string,
  redirectOnAnswer: PropTypes.bool,
};
export default function Question({ sectionId, question, departmentId }) {
  const { modifyQuestion, setErrorsTags } = useFormContext();
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();
  const { showModal, hideModal } = useModal();

  const [selectedInput, setSelectedInput] = useState(question.type ?? 'TextInput');
  const [tabValue, setTabValue] = useState(question.attributeId ? 1 : 0);
  // eslint-disable-next-line
  const [attributes, setAttributes] = useState([]);
  const [inputValue, setInputValue] = useState(question.attributeName || '');

  const isSelectOrRadio =
    selectedInput === 'SelectInput' || selectedInput === 'RadioInput';

  const handleRedirectToggle = (e) => {
    modifyQuestion(question.id, { ...question, redirectOnAnswer: e.target.checked });
  };

  const fetchAttributes = useCallback(() => {
    if (departmentId) fetchTagsByDepartment(departmentId).then(setAttributes);
  }, [departmentId]);

  useEffect(() => {
    if (tabValue === 1) fetchAttributes();
  }, [tabValue, fetchAttributes]);

  const isInputUnregistered = useMemo(() => {
    if (tabValue !== 1 || !inputValue.trim()) return false;
    return !question.attributeId;
  }, [tabValue, inputValue, question.attributeId]);

  useEffect(() => {
    if (setErrorsTags) {
      setErrorsTags((prev) => ({
        ...prev,
        [question.id]: isInputUnregistered ? true : undefined,
      }));
    }
  }, [isInputUnregistered, question.id, setErrorsTags]);

  const handleCreateAttributeClick = (event) => {
    event.preventDefault();
    showModal({
      title: t('pages.tagsPage.addNewTag'),
      Component: AddUpdateTags,
      props: {
        hideModal,
        initialData: { name: inputValue, departmentId },
        handleFetchTags: (newTag) => {
          if (newTag) {
            setAttributes((prev) => [...prev, newTag]);
            modifyQuestion(question.id, {
              ...question,
              attributeId: newTag.id,
              attributeName: newTag.name,
            });
          }
        },
      },
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) {
      modifyQuestion(question.id, {
        ...question,
        attributeId: null,
        attributeName: null,
      });
      setInputValue('');
    }
  };

  const Inputs = [
    { text: t('formInputs.text'), value: 'TextInput' },
    { text: t('formInputs.textArea'), value: 'TextAreaInput' },
    { text: t('formInputs.select'), value: 'SelectInput' },
    { text: t('formInputs.checkbox'), value: 'CheckboxInput' },
    { text: t('formInputs.multipleChoice'), value: 'RadioInput' },
    { text: t('formInputs.date'), value: 'DateTimePickerInput' },
    { text: t('formInputs.fileUpload'), value: 'FileInput' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Tab Row Flex Wrapper */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          flexWrap: isSmallScreen ? 'wrap' : 'nowrap',
          gap: 2,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            // Removes double-border overlaps
            '& .MuiTabs-flexContainer': { border: 'none' },
          }}
        >
          <Tab
            label={t('pages.formPage.question')}
            sx={{ fontSize: '0.8rem', fontWeight: 700 }}
          />
          <Tab label={t('form.attribute')} sx={{ fontSize: '0.8rem', fontWeight: 700 }} />
        </Tabs>

        {/* Dynamic Branching Toggle placed cleanly on the right edge of the header */}
        {isSelectOrRadio && (
          <FormControlLabel
            label={t('pages.formPage.goToSectionBaseOn')}
            labelPlacement="start" // Places text on the left of the switch for cleaner layout
            control={
              <Switch
                checked={question.redirectOnAnswer ?? false}
                onChange={handleRedirectToggle}
                size="small"
              />
            }
            sx={{
              mr: 1,
              mb: isSmallScreen ? 1.5 : 0.5,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.8rem',
                fontWeight: 700,
                color: 'text.secondary',
              },
            }}
          />
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Question text (reverted back to standard full 8 columns on desktop) */}
        <Grid item xs={isSmallScreen ? 12 : 8}>
          <TextInput
            label={t('pages.formPage.question')}
            variant="standard"
            fullWidth
            defaultValue={question.text || ''}
            onBlur={(e) =>
              modifyQuestion(question.id, { ...question, text: e.target.value })
            }
          />
        </Grid>

        {/* Input type selector (reverted back to standard full 4 columns on desktop) */}
        <Grid item xs={isSmallScreen ? 12 : 4}>
          <SelectInput
            labelValue={t('pages.formPage.type')}
            defaultValue={selectedInput}
            options={Inputs}
            setSelectedInput={(val) => {
              setSelectedInput(val);
              // Reset redirectOnAnswer when input type changes away from select/radio
              if (val !== 'SelectInput' && val !== 'RadioInput') {
                modifyQuestion(question.id, {
                  ...question,
                  type: val,
                  redirectOnAnswer: false,
                });
              } else {
                modifyQuestion(question.id, { ...question, type: val });
              }
            }}
          />
        </Grid>

        {/* Attribute Autocomplete Selection & Dynamic Creation Link */}
        {tabValue === 1 && (
          <Grid item xs={12}>
            <Autocomplete
              freeSolo // Allows custom typed values and prevents the input from being cleared on blur
              options={attributes}
              getOptionLabel={(option) => option.name || option} // Handles both object and raw string states
              value={attributes.find((a) => a.id === question.attributeId) || null}
              onInputChange={(e, v) => setInputValue(v)}
              onChange={(e, nv) => {
                // Safely handle both autocomplete selections (objects) and custom typed selections (strings)
                const resolvedVal = typeof nv === 'string' ? { id: null, name: nv } : nv;
                modifyQuestion(question.id, {
                  ...question,
                  attributeId: resolvedVal?.id || null,
                  attributeName: resolvedVal?.name || null,
                });
              }}
              noOptionsText={null} // Hides the "No options" popup entirely to keep the "+ Add Attribute" button visible
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('form.attributeName')}
                  variant="standard"
                  error={isInputUnregistered}
                  helperText={
                    isInputUnregistered ? t('formControl.mustCreateAttribute') : ''
                  }
                />
              )}
            />
            {isInputUnregistered && (
              <Link
                component="button"
                variant="caption"
                onMouseDown={handleCreateAttributeClick} // Fires instantly on mousedown before input blur clears the value
                sx={{
                  fontWeight: 800,
                  textDecoration: 'none',
                  color: '#00A76F',
                  textAlign: 'left',
                  mt: 0.5,
                }}
              >
                {`+ ${t('buttons.add')} "${inputValue}"`}
              </Link>
            )}
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 2 }}>
        <InputPlaceHolder
          selectedInput={selectedInput}
          question={question}
          sectionId={sectionId}
          redirectOnAnswer={question.redirectOnAnswer}
        />
      </Box>
    </Box>
  );
}

Question.propTypes = {
  question: PropTypes.object,
  sectionId: PropTypes.string,
  departmentId: PropTypes.number,
};
